import { useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import { fetchAlerts } from "../services/fetchData.js";
import rt from "../services/RealtimeData";
import Alert from "./Alert.jsx";

/**
 * RiderAlerts
 *
 * Fetches rider alerts once, filters them by route,
 * and renders dismissible Bootstrap alerts.
 */
export default function RiderAlerts({ routeId = null, emptyMessage = null }) {
  /* ------------------------------------------------------------------
   * State
   * ------------------------------------------------------------------ */
  const [allAlerts, setAllAlerts] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const [expandedAlerts, setExpandedAlerts] = useState({});

  /* ------------------------------------------------------------------
   * Effects
   * ------------------------------------------------------------------ */

  // Fetch all alerts once on mount
  useEffect(() => {
    let active = true;

    (async () => {
      try {
        const data = await fetchAlerts();
        if (active) {
          setAllAlerts(Array.isArray(data) ? data : []);
        }
      } catch (error) {
        if (active) {
          setAllAlerts([]);
        }
        console.error("Error loading alerts:", error);
      } finally {
        if (active) {
          setIsLoaded(true);
        }
      }
    })();

    return () => {
      active = false;
    };
  }, []);

  // Recompute alerts whenever routeId or alert data changes
  useEffect(() => {
    const routeAlerts = rt.getAlerts(allAlerts, routeId);
    setAlerts(Array.isArray(routeAlerts) ? routeAlerts : []);
  }, [routeId, allAlerts]);

  // Collapse alerts when the route changes
  useEffect(() => {
    setExpandedAlerts({});
  }, [routeId]);

  /* ------------------------------------------------------------------
   * Render
   * ------------------------------------------------------------------ */
  if (!Array.isArray(alerts) || alerts.length === 0) {
    return isLoaded && emptyMessage ? <p>{emptyMessage}</p> : null;
  }
  return (
    <>
      {alerts.map((message) => {
        const expanded = !!expandedAlerts[message?.nid];
        const bodyId = `rider-alert-body-${message?.nid}`;
        const toggleAlert = () => {
          setExpandedAlerts((prev) => ({
            ...prev,
            [message.nid]: !expanded,
          }));
        };

        return (
          <Alert
            key={message?.nid}
            className="alert-collapsible"
            variant="warning"
          >
            <div className={expanded ? "alert-heading expanded" : "alert-heading"}>
              <strong className="alert-title">
                <i
                  className="fas fa-exclamation-triangle"
                  title="Service alert"
                />{" "}
                {message.title}
              </strong>
              <Button
                variant="link"
                className="alert-toggle"
                aria-expanded={expanded}
                aria-controls={bodyId}
                aria-label={expanded ? "Collapse alert" : "Expand alert"}
                onClick={toggleAlert}
              >
                <i
                  className={expanded ? "fas fa-minus" : "fas fa-plus"}
                  aria-hidden="true"
                />
              </Button>
            </div>

            <div
              id={bodyId}
              className={expanded ? "alert-body expanded" : "alert-body"}
            >
              <div dangerouslySetInnerHTML={{ __html: message.body }} />
              <Button
                variant="link"
                className="p-0 mt-2"
                onClick={toggleAlert}
              >
                Show less
              </Button>
            </div>
          </Alert>
        );
      })}
    </>
  );
}
